// src/components/ListView.jsx
import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Header from './Header';
import Item from './Item';
import ListStateBar from './ListStateBar';
import ListMetricsBar from './ListMetricsBar';
import ListMetricsModal from './ListMetricsModal';
import SearchAddBar from './SearchAddBar';
import SearchOverlay from './SearchOverlay';
import QuickEditModal from './QuickEditModal';
import ConfirmModal from './ConfirmModal';
import { useGroceries } from '../hooks/useGroceries';
import { useCategories } from '../hooks/useCategories';
import { useItemOperations } from '../hooks/useItemOperations';
import { useItemFilters } from '../hooks/useItemFilters';
import { useListCalculations } from '../hooks/useListCalculations';
import { getTodayDateString, getListsWithUncheckedCounts } from '../utils/itemUtils';
import { normalizeListSortKey } from '../i18n/sortKeys';

const STORAGE_KEY = 'list_sort_preference';
const COLLAPSED_CATEGORIES_KEY = 'collapsed_categories';
const COLLAPSED_SAVED_FOR_LATER_KEY = 'collapsed_saved_for_later';
// Tiempo (ms) que el orden de la lista se queda "congelado" después de checar/des-checar
// un item, para evitar que el item recién tocado salte de posición de inmediato.
const REORDER_FREEZE_MS = 2000;

const ListView = memo(({
  showToast,
  lists,
  activeList,
  activeListId,
  onListChange,
  onNewList,
  onRefreshLists
}) => {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return normalizeListSortKey(saved);
  });
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para Change list modal
  const [showChangeListModal, setShowChangeListModal] = useState(false);
  const [changingListItem, setChangingListItem] = useState(null);
  
  // Estado para Metrics Modal
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  // Estado para forzar re-render cuando cambian taxRate/budget desde localStorage
  const [metricsKey, setMetricsKey] = useState(0);
  
  // Estado para categorías colapsadas
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    const saved = localStorage.getItem(COLLAPSED_CATEGORIES_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const [isSavedForLaterCollapsed, setIsSavedForLaterCollapsed] = useState(() => {
    const saved = localStorage.getItem(COLLAPSED_SAVED_FOR_LATER_KEY);
    return saved ? saved === 'true' : false;
  });

  // Snapshot de IDs en su orden actual mientras el reorder está "congelado".
  // Si está en null, el orden se calcula normalmente con sortBy.
  const [frozenOrderIds, setFrozenOrderIds] = useState(null);
  const freezeTimerRef = useRef(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: '',
    variant: 'primary',
    onConfirm: null,
  });

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  }, []);

  const { allItems, loading, error, fetchItems, addNewItem, updateItem, deleteItem, setAllItems } = useGroceries(showToast);
  const { availableCategories, loading: categoriesLoading, error: categoriesError, fetchCategories, updateCategories, addNewCategory } = useCategories(showToast);
  const { checkToggleItem, updateQuantity, addToList, removeFromList, updateName, updatePrice, updateNote, updateCategory, updateList, updateLater } = useItemOperations(showToast);
  const { groupItemsByCategory } = useItemFilters();
  const { getListItems, isItemInList } = useListCalculations();

  // Guardar preferencia de sort en localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, sortBy);
  }, [sortBy]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchItems(), fetchCategories()]);
    };
    loadData();
  }, [fetchItems, fetchCategories]);

  // Escuchar cambios en localStorage para taxRate y budget
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'list_tax_rate' || e.key === 'list_budget') {
        setMetricsKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Guardar estado de categorías colapsadas en localStorage
  useEffect(() => {
    localStorage.setItem(COLLAPSED_CATEGORIES_KEY, JSON.stringify(collapsedCategories));
  }, [collapsedCategories]);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_SAVED_FOR_LATER_KEY, String(isSavedForLaterCollapsed));
  }, [isSavedForLaterCollapsed]);

  useEffect(() => {
    return () => {
      if (freezeTimerRef.current) {
        clearTimeout(freezeTimerRef.current);
        freezeTimerRef.current = null;
      }
    };
  }, []);

  const activeListName = activeList?.name || t('common.selected');

  const listsWithCounts = useMemo(
    () => getListsWithUncheckedCounts(lists, allItems),
    [lists, allItems]
  );

  const activeListWithCount = useMemo(
    () => listsWithCounts.find(list => list.id === activeListId) || activeList,
    [listsWithCounts, activeListId, activeList]
  );

  // Obtener items de la lista activa con ordenamiento aplicado
  const activeListItems = allItems.filter(item => {
    return isItemInList(item) && item.list_id === activeListId;
  });

  const listItems = activeListItems.filter(item => parseInt(item.later) !== 1);
  const savedForLaterItems = activeListItems.filter(item => parseInt(item.later) === 1);

  const sortedListItems = getListItems(listItems, sortBy);
  // Si hay un snapshot congelado, respetamos ese orden y agregamos al final
  // cualquier item nuevo que haya aparecido durante el freeze.
  const items = frozenOrderIds
    ? (() => {
        const idToItem = new Map(listItems.map(it => [it.id, it]));
        const ordered = [];
        for (const id of frozenOrderIds) {
          const it = idToItem.get(id);
          if (it) {
            ordered.push(it);
            idToItem.delete(id);
          }
        }
        for (const it of idToItem.values()) ordered.push(it);
        return ordered;
      })()
    : sortedListItems;
  const { grouped: groupedItems, sortedCategories } = groupItemsByCategory(items, availableCategories);

  const startReorderFreeze = useCallback(() => {
    setFrozenOrderIds(prev => prev !== null ? prev : sortedListItems.map(i => i.id));
    if (freezeTimerRef.current) clearTimeout(freezeTimerRef.current);
    freezeTimerRef.current = setTimeout(() => {
      setFrozenOrderIds(null);
      freezeTimerRef.current = null;
    }, REORDER_FREEZE_MS);
  }, [sortedListItems]);

  const handleRefresh = () => {
    fetchItems();
    fetchCategories();
    showToast(t('toasts.refreshing'), 'info');
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const handleOpenMetricsModal = () => {
    setShowMetricsModal(true);
  };

  const handleCloseMetricsModal = () => {
    setShowMetricsModal(false);
  };

  const handleMetricsSave = (data) => {
    setMetricsKey(prev => prev + 1);
    showToast(t('toasts.metricsUpdated'), 'success');
  };

  const toggleCategory = useCallback((category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  }, []);

  const doClearCheckedItems = useCallback(async (checkedItems) => {
    const previousAllItems = [...allItems];
    let successCount = 0;
    let errorCount = 0;

    try {
      setAllItems(prev => prev.map(item => {
        if (checkedItems.some(ci => ci.id === item.id)) {
          return {
            ...item,
            quantity: 0,
            list_id: null,
            list_name: ''
          };
        }
        return item;
      }));

      for (const item of checkedItems) {
        try {
          await removeFromList(item);
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Error clearing item ${item.id}:`, err);
        }
      }

      if (successCount > 0) {
        showToast(t('toasts.clearedChecked', { count: successCount, listName: activeListName }), 'success');
      }
      if (errorCount > 0) {
        showToast(t('toasts.failedClear', { count: errorCount }), 'error');
      }
      if (successCount > 0 && onRefreshLists) {
        onRefreshLists();
      }

    } catch (err) {
      console.error('Error clearing checked items:', err);
      setAllItems(previousAllItems);
      showToast(t('toasts.errorClearing', { msg: err.message }), 'error');
    }
  }, [allItems, activeListName, removeFromList, showToast, setAllItems, onRefreshLists, t]);

  const handleClearCheckedItems = useCallback(() => {
    const checkedItems = allItems.filter(item => {
      return isItemInList(item) && item.list_id === activeListId && parseInt(item.status) === 1 && parseInt(item.later) !== 1;
    });

    if (checkedItems.length === 0) {
      showToast(t('toasts.noCheckedToClear'), 'info');
      return;
    }

    const count = checkedItems.length;
    setConfirmDialog({
      isOpen: true,
      title: t('confirm.clearCheckedTitle'),
      message: (
        <Trans
          i18nKey={count > 1 ? 'confirm.clearCheckedMessage_plural' : 'confirm.clearCheckedMessage'}
          values={{ count, listName: activeListName }}
          components={{ strong: <strong /> }}
        />
      ),
      confirmLabel: t('common.remove'),
      variant: 'danger',
      onConfirm: () => {
        closeConfirmDialog();
        doClearCheckedItems(checkedItems);
      },
    });
  }, [allItems, activeListId, activeListName, isItemInList, showToast, closeConfirmDialog, doClearCheckedItems, t]);

  const handleUpdateItem = async (itemId, updateData) => {
    const previousAllItems = [...allItems];
    
    try {
      const itemToUpdate = allItems.find(item => item.id === itemId);
      if (!itemToUpdate) throw new Error('Item not found');

      setAllItems(prev => prev.map(item => item.id === itemId ? { ...item, ...updateData } : item));

      let updatedData;
      if ('item' in updateData) {
        updatedData = await updateName(itemId, updateData.item, itemToUpdate);
      } else if ('price' in updateData) {
        updatedData = await updatePrice(itemId, updateData.price, itemToUpdate);
      } else if ('category' in updateData) {
        updatedData = await updateCategory(itemId, updateData.category, itemToUpdate);
        if (updateData.category && !availableCategories.includes(updateData.category)) {
          addNewCategory(updateData.category);
        }
      } else if ('note' in updateData) {
        updatedData = await updateNote(itemId, updateData.note, itemToUpdate);
      } else if ('list_id' in updateData) {
        updatedData = await updateList(itemId, updateData.list_id, itemToUpdate);
      } else if ('later' in updateData) {
        updatedData = await updateLater(itemToUpdate, updateData.later);
      } else {
        throw new Error('Invalid update data');
      }

      setAllItems(prev => prev.map(item => item.id === itemId ? { ...item, ...updatedData } : item));

    } catch (err) {
      console.error('Error updating item:', err);
      setAllItems(previousAllItems);
      showToast(t('toasts.errorUpdating', { msg: err.message }), 'error');
    }
  };

  const handleCheckToggle = async (id) => {
    const previousAllItems = [...allItems];

    startReorderFreeze();

    try {
      const itemToUpdate = allItems.find(item => item.id === id);
      if (!itemToUpdate) throw new Error('Item not found');
      
      const currentStatus = parseInt(itemToUpdate.status);
      const newStatus = currentStatus === 1 ? 0 : 1;
      
      setAllItems(prev => prev.map(item => item.id === id ? { 
        ...item, 
        status: newStatus.toString(),
        date: newStatus === 1 ? getTodayDateString() : itemToUpdate.date
      } : item));
      
      const updatedData = await checkToggleItem(itemToUpdate);
      
      setAllItems(prev => prev.map(item => item.id === id ? { 
        ...item, 
        status: updatedData.status, 
        date: updatedData.date || item.date 
      } : item));
      
    } catch (err) {
      console.error('Error toggling item:', err);
      setAllItems(previousAllItems);
      showToast(t('toasts.errorToggling', { msg: err.message }), 'error');
    }
  };

  const handleQuantityChange = async (id, newQuantity) => {
    const previousAllItems = [...allItems];
    
    try {
      const itemToUpdate = allItems.find(item => item.id === id);
      if (!itemToUpdate) throw new Error('Item not found');
      
      setAllItems(prev => prev.map(item => item.id === id ? { ...item, quantity: newQuantity } : item));
      
      await updateQuantity(itemToUpdate, newQuantity);
      
    } catch (err) {
      console.error('Error updating quantity:', err);
      setAllItems(previousAllItems);
      showToast(t('toasts.errorQuantity', { msg: err.message }), 'error');
    }
  };

  const handleRemoveFromList = async (id) => {
    const previousAllItems = [...allItems];
    
    try {
      const itemToUpdate = allItems.find(item => item.id === id);
      if (!itemToUpdate) throw new Error('Item not found');
      
      setAllItems(prev => prev.map(item => item.id === id ? { 
        ...item, 
        quantity: 0, 
        list_id: null,
        list_name: ''
      } : item));
      
      await removeFromList(itemToUpdate);
      showToast(t('toasts.itemRemoved'), 'success');
      if (onRefreshLists) onRefreshLists();
      
    } catch (err) {
      console.error('Error removing from list:', err);
      setAllItems(previousAllItems);
      showToast(t('toasts.errorRemoving', { msg: err.message }), 'error');
    }
  };

  const handleAddToList = async (id) => {
    const previousAllItems = [...allItems];
    
    try {
      if (!activeList) throw new Error('No active list selected');
      const itemToUpdate = allItems.find(item => item.id === id);
      if (!itemToUpdate) throw new Error('Item not found');
      
      setAllItems(prev => prev.map(item => item.id === id ? { 
        ...item, 
        quantity: 1, 
        status: "0",
        list_id: activeListId,
        list_name: activeListName
      } : item));
      
      await addToList(itemToUpdate, activeList);
      showToast(t('toasts.itemAdded'), 'success');
      if (onRefreshLists) onRefreshLists();
      
    } catch (err) {
      console.error('Error adding to list:', err);
      setAllItems(previousAllItems);
      showToast(t('toasts.errorAdding', { msg: err.message }), 'error');
    }
  };

  const doDeleteItem = async (id) => {
    const previousAllItems = [...allItems];
    try {
      setAllItems(prev => prev.filter(item => item.id !== id));
      await deleteItem(id);
      showToast(t('toasts.itemDeleted'), 'success');
    } catch (err) {
      console.error('Error deleting item:', err);
      setAllItems(previousAllItems);
      showToast(t('toasts.errorDeleting', { msg: err.message }), 'error');
    }
  };

  const handleDeleteItem = (id) => {
    const target = allItems.find(it => it.id === id);
    const itemName = target?.item || t('common.thisItem');
    setConfirmDialog({
      isOpen: true,
      title: t('confirm.deleteItemTitle'),
      message: (
        <Trans
          i18nKey="confirm.deleteItemMessage"
          values={{ itemName }}
          components={{ strong: <strong /> }}
        />
      ),
      confirmLabel: t('common.delete'),
      variant: 'danger',
      onConfirm: () => {
        closeConfirmDialog();
        doDeleteItem(id);
      },
    });
  };

  const handleChangeList = (item) => {
    setChangingListItem(item);
    setShowChangeListModal(true);
  };

  const handleChangeListSave = async (updateData) => {
    if (!changingListItem) return;
    
    try {
      if (updateData.newList) {
        await onNewList(updateData.newList);
        if (onRefreshLists) await onRefreshLists();
        setShowChangeListModal(false);
        setChangingListItem(null);
        return;
      }

      const newListId = updateData.list_id;
      if (!newListId) throw new Error('List is required');
      
      await handleUpdateItem(changingListItem.id, {
        list_id: newListId,
        list_name: updateData.list_name || ''
      });
      setShowChangeListModal(false);
      setChangingListItem(null);
      showToast(t('toasts.itemMoved', { listName: updateData.list_name || t('common.list') }), 'success');
      if (onRefreshLists) onRefreshLists();
      
    } catch (err) {
      showToast(t('toasts.errorChangingList', { msg: err.message }), 'error');
    }
  };

  const handleAddToCatalog = async (itemData) => {
    try {
      const newItem = await addNewItem(itemData, showToast);
      
      if (newItem.category && !availableCategories.includes(newItem.category)) {
        addNewCategory(newItem.category);
      }
      
      return newItem;
    } catch (err) {
      throw err;
    }
  };

  const handleNewListClick = () => {
    setShowChangeListModal(true);
    setChangingListItem({ id: 'new-list', item: t('list.newListPlaceholder') });
  };

  const handleSearchFocus = () => {
    setShowSearchOverlay(true);
  };

  const isLoading = loading || categoriesLoading;
  const hasError = error || categoriesError;

  const renderCategoryGroups = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-3"></div>
            <p>{t('list.loading')}</p>
          </div>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className="p-8 text-center">
          <div className="text-red-600 mb-2">⚠️ {error || categoriesError}</div>
          <button 
            onClick={() => {
              fetchItems();
              fetchCategories();
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            {t('common.tryAgain')}
          </button>
        </div>
      );
    }

    if (Object.keys(groupedItems).length === 0 && savedForLaterItems.length === 0) {
      return (
        <div className="p-8 text-center text-gray-500">
          <div className="mb-6">
            <i className="fas fa-shopping-cart text-gray-300 text-6xl mx-auto mb-4 block"></i>
            <p className="mb-4">{t('list.emptyTitle', { listName: activeListName })}</p>
            <p className="text-sm text-gray-600 mb-4">
              {t('list.emptyHint')}
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        {sortedCategories.map(category => {
      const isCollapsed = collapsedCategories[category] || false;
      const displayCategory = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
      
      return (
        <div key={category} className="mb-0">
          {/* Category Header - Clickable */}
          <div 
            className="sticky top-[65px] bg-gray-200 px-3 py-2 border-b border-gray-200 z-10 flex justify-between items-center cursor-pointer hover:bg-gray-300 transition-colors"
            onClick={() => toggleCategory(category)}
          >
            <h3 className="font-semibold text-gray-700 text-sm tracking-wider">
              {displayCategory}
            </h3>
            <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'} text-gray-500 text-xs transition-transform`}></i>
          </div>
          
          {/* Category Items - Collapsible */}
          {!isCollapsed && (
            <div>
              {groupedItems[category].map(item => (
                <Item
                  key={item.id}
                  item={item}
                  isInList={true}
                  showDaysSince={false}
                  onCheckToggle={handleCheckToggle}
                  onDelete={handleDeleteItem}
                  onEdit={(item) => handleUpdateItem(item.id, { item: item.item })}
                  onQuantityChange={handleQuantityChange}
                  onAddToList={handleAddToList}
                  onRemoveFromList={handleRemoveFromList}
                  onChangeList={handleChangeList}
                  showToast={showToast}
                  onUpdateItem={handleUpdateItem}
                  availableCategories={availableCategories}
                  currentView="list"
                  activeList={activeList}
                />
              ))}
            </div>
          )}
        </div>
      );
        })}

        {savedForLaterItems.length > 0 && (
          <div className="mt-2">
            <div
              className="sticky top-[65px] bg-amber-100 px-3 py-2 border-b border-amber-200 z-10 flex justify-between items-center cursor-pointer hover:bg-amber-200 transition-colors"
              onClick={() => setIsSavedForLaterCollapsed(prev => !prev)}
            >
              <h3 className="font-semibold text-amber-800 text-sm tracking-wider">
                {t('list.savedForLater', { count: savedForLaterItems.length })}
              </h3>
              <i className={`fas fa-chevron-${isSavedForLaterCollapsed ? 'right' : 'down'} text-amber-700 text-xs transition-transform`}></i>
            </div>

            {!isSavedForLaterCollapsed && (
              <div className="bg-amber-50">
                {savedForLaterItems.map(item => (
                  <Item
                    key={item.id}
                    item={item}
                    isInList={true}
                    showDaysSince={false}
                    onCheckToggle={handleCheckToggle}
                    onDelete={handleDeleteItem}
                    onEdit={(item) => handleUpdateItem(item.id, { item: item.item })}
                    onQuantityChange={handleQuantityChange}
                    onAddToList={handleAddToList}
                    onRemoveFromList={handleRemoveFromList}
                    onChangeList={handleChangeList}
                    showToast={showToast}
                    onUpdateItem={handleUpdateItem}
                    availableCategories={availableCategories}
                    currentView="list"
                    activeList={activeList}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-32">
      <Header
        showCatalogButton={true}
        onRefresh={handleRefresh}
      />

      {/* Search bar */}
      <div className="p-2 bg-white border-b border-gray-200">
        <SearchAddBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onAdd={(term) => {
            setSearchTerm(term);
            setShowSearchOverlay(true);
          }}
          onFocus={handleSearchFocus}
          onClear={() => setSearchTerm('')}
          isSaving={false}
        />
      </div>

      <ListStateBar 
        onSortChange={handleSortChange}
        currentSort={sortBy}
        lists={listsWithCounts}
        activeList={activeListWithCount}
        onListChange={onListChange}
        onNewListClick={handleNewListClick}
        onOpenMetricsModal={handleOpenMetricsModal}
        onClearCheckedItems={handleClearCheckedItems}
      />

      {/* Metrics Bar (sticky) */}
      <div className="sticky top-0 z-30 bg-white">
        <ListMetricsBar 
          key={metricsKey}
          items={listItems}
          onOpenModal={handleOpenMetricsModal}
        />
      </div>

      <div className="divide-y divide-gray-100 pb-6">
        {renderCategoryGroups()}
      </div>

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={showSearchOverlay}
        onClose={() => setShowSearchOverlay(false)}
        context="list"
        allItems={allItems}
        onAddToList={handleAddToList}
        onAddToCatalog={handleAddToCatalog}
        onEditItem={(item) => handleUpdateItem(item.id, { item: item.item })}
        onDeleteItem={handleDeleteItem}
        onQuantityChange={handleQuantityChange}
        onCheckToggle={handleCheckToggle}
        onChangeList={handleChangeList}
        onUpdateItem={handleUpdateItem}
        showToast={showToast}
        availableCategories={availableCategories}
        activeList={activeList}
        activeListId={activeListId}
        lists={lists}
      />

      {/* Change List Modal */}
      {showChangeListModal && (
        <QuickEditModal
          isOpen={showChangeListModal}
          onClose={() => {
            setShowChangeListModal(false);
            setChangingListItem(null);
          }}
          field="list"
          context={changingListItem?.id === 'new-list' ? 'newList' : 'list'}
          currentValue=""
          itemId={changingListItem?.id}
          itemName={changingListItem?.item}
          onSaveSuccess={handleChangeListSave}
          showToast={showToast}
          availableLists={lists}
        />
      )}

      {/* Metrics Modal */}
      <ListMetricsModal
        isOpen={showMetricsModal}
        onClose={handleCloseMetricsModal}
        onSave={handleMetricsSave}
        items={listItems}
        initialSelectedMetrics={JSON.parse(localStorage.getItem('list_selected_metrics') || 'null')}
        initialTaxRate={(() => {
          const v = parseFloat(localStorage.getItem('list_tax_rate'));
          return Number.isFinite(v) ? v : undefined;
        })()}
        initialBudget={(() => {
          const v = parseFloat(localStorage.getItem('list_budget'));
          return Number.isFinite(v) ? v : undefined;
        })()}
        showToast={showToast}
      />

      <ConfirmModal
        isOpen={confirmDialog.isOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
      />
    </div>
  );
});

export default ListView;